import React, { useState, useRef, useEffect } from 'react';
import {
  FileText, Calendar, User, ChevronLeft, ChevronRight,
  Bug, Sparkles, Wrench, Shield, Clock, Tag, ChevronDown, ChevronUp
} from 'lucide-react';

const ITEMS_PER_PAGE = 5;
const AUTHOR = 'Altaf Rehman — AI Engineer, BrainCX';

const TYPE_CONFIG = {
  fix: { label: 'Bug Fix', icon: Bug, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  feature: { label: 'Feature', icon: Sparkles, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  enhancement: { label: 'Enhancement', icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  security: { label: 'Security', icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  performance: { label: 'Performance', icon: Clock, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
};

// ──────────────────────────────────────────────────────────────────────
//  RELEASE CONTENT — update this section before each push
// ──────────────────────────────────────────────────────────────────────
const RELEASES = [
  {
    version: '1.11.0',
    date: '2026-05-01',
    title: 'Twilio & Vonage SIP, Agent Lookup Routing, Caller ID & Custom API Keys',
    items: [
      {
        ticket: 'BCX-463',
        title: 'Agent Lookup Routing',
        type: 'feature',
        description:
          'Introduced agent lookup routing, allowing the platform to dynamically resolve and route incoming calls to the correct agent based on configurable lookup criteria. This eliminates the need for hardcoded agent assignments and enables flexible, data-driven call distribution across multi-agent deployments.',
      },
      {
        ticket: 'BCX-419',
        title: 'Caller ID & Route for Easy Call Lookups',
        type: 'feature',
        description:
          'Added Caller ID capture and routing support so that inbound calls can be matched against known contacts or CRM records in real time. Agents now receive caller context before the conversation begins, and administrators can define routing rules based on the incoming phone number for faster, smarter call handling.',
      },
      {
        ticket: 'BCX-445',
        title: 'Twilio Integration',
        type: 'feature',
        description:
          'Delivered full Twilio telephony integration, enabling organizations to provision and manage Twilio phone numbers directly within BrainCX and route inbound and outbound calls through Twilio\'s infrastructure. The integration supports SIP trunking, number management, and webhook-based call event handling alongside the existing Telnyx and LiveKit stacks.',
      },
      {
        ticket: 'BCX-426',
        title: 'Vonage SIP Integration',
        type: 'feature',
        description:
          'Added Vonage SIP trunk integration as an additional carrier option for voice agents. Organizations can now connect their Vonage accounts to route calls through Vonage\'s global SIP network, providing carrier redundancy and geographic flexibility alongside the existing Telnyx and Twilio integrations.',
      },
      {
        ticket: 'BCX-354',
        title: 'Custom API Keys for ElevenLabs, Deepgram & Cartesia per Organization',
        type: 'feature',
        description:
          'Organizations can now supply their own API keys for ElevenLabs, Deepgram, and Cartesia at the organization level, allowing usage to be billed directly to their accounts and keeping consumption separate from the BrainCX shared quota. Each service independently falls back to the BrainCX platform key when no custom key is configured, so existing agents continue to work without any migration required.',
      },
      {
        ticket: 'BCX-278',
        title: 'Phone Number Management via Infrastructure API',
        type: 'feature',
        description:
          'Implemented direct phone number provisioning and management through the infrastructure API layer. Administrators can now search, purchase, assign, and release phone numbers programmatically without leaving the platform, with changes reflected immediately across agent routing and SIP trunk configuration.',
      },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-04-20',
    title: 'Remember My Device & Persistent Session Store',
    items: [
      {
        ticket: 'BCX-353',
        title: 'Remember This Device for 30 Days',
        type: 'feature',
        description:
          'Added a "Remember this device for 30 days" checkbox to the Developer Login and Super Admin Login screens. When checked, the session cookie and server-side session are extended to 30 days instead of the default 24 hours. The preference is carried through the 2FA verification step server-side, so no extra input is required after entering the verification code.',
      },
      {
        ticket: 'BCX-354',
        title: 'Persistent Session Store — Survive Server Restarts',
        type: 'security',
        description:
          'Resolved the root cause of daily re-logins: sessions were previously held only in process memory and lost on every deploy or server restart. All sessions are now written to a new persistent_sessions table in PostgreSQL on creation and reloaded into memory on startup. Expired rows are automatically pruned on each boot. This applies to developer, super admin, and admin sessions alike.',
      },
      {
        ticket: 'BCX-355',
        title: 'Immediate Session Revocation Covers Persisted Sessions',
        type: 'security',
        description:
          'Strengthened the developer access revocation flow so that revoking a developer instantly removes all of their sessions from both the in-memory store and the persistent_sessions DB table. Previously, a 30-day cookie issued before revocation could have been reloaded from the database after a server restart, allowing the developer to regain access. That gap is now fully closed.',
      },
      {
        ticket: 'BCX-356',
        title: 'Revoke & Restore Developer Login Access',
        type: 'feature',
        description:
          'Super admins can now revoke or restore a developer\'s login access directly from the Developers table in the Super Admin panel. Revoking access immediately invalidates all active sessions for that developer across every device — they are signed out in real time and cannot log back in until access is restored. A clear visual indicator shows each developer\'s current access status.',
      },
      {
        ticket: 'BCX-357',
        title: 'Last Month Filter in Super Admin Usage Analytics',
        type: 'feature',
        description:
          'Added a "Last Month" option to the date range filter in the Super Admin usage analytics panel, allowing admins to view the complete previous calendar month\'s usage in a single click. Previously only Today, This Week, and This Month were available, making month-over-month comparisons require manual date entry.',
      },
      {
        ticket: 'BCX-358',
        title: '404 Not Found Pages on Invalid Routes',
        type: 'feature',
        description:
          'Implemented dedicated 404 error pages that are displayed whenever a user navigates to a route that does not exist. Previously, unmatched routes resulted in a blank screen or an unhandled rendering error. The new pages provide a clear message and a navigation link back to the appropriate section of the platform.',
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-04-10',
    title: 'Persistent Variable Editors, Phone Cleanup & Outbound SIP Fix',
    items: [
      {
        ticket: 'BCX-350',
        title: 'Persistent Key-Value Variable Editors',
        type: 'feature',
        description:
          'Replaced the raw JSON text areas for test-call variables, outbound call variables, and outbound overrides with user-friendly key-value pair editors. Entries are automatically persisted in localStorage per agent so they survive page reloads and modal re-opens. A toggleable JSON preview lets users verify the payload before initiating a call.',
      },
      {
        ticket: 'BCX-351',
        title: 'Phone Number Removal with LiveKit SIP Cleanup',
        type: 'enhancement',
        description:
          'Added a Remove button to the Telnyx Phone Management table that deletes a phone number and automatically cleans up the associated LiveKit SIP resources — inbound trunk, outbound trunk, and dispatch rule — in a single action. The button shows a loading spinner during the operation and reports per-resource cleanup results in the debug log.',
      },
      {
        ticket: 'BCX-352',
        title: 'Outbound SIP Trunk Address Correction',
        type: 'fix',
        description:
          'Fixed the outbound SIP trunk creation to use sip.telnyx.com as the trunk address instead of deriving it from the LiveKit URL. The previous derivation produced an incorrect endpoint, causing outbound calls routed through Telnyx to fail to connect.',
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-04-01',
    title: 'Call History CSV Export & LiveAvatar Full-Screen Products Embed',
    items: [
      {
        ticket: 'BCX-346',
        title: 'CSV Export Functionality',
        type: 'feature',
        description:
          'Delivered Call History & Export in the dashboard: filter calls by agent and date range, view the same conversation fields as the main sessions table plus post-call summary and per-agent data extraction columns, optional column visibility controls, and CSV export with selectable columns. Fixed individual column toggles in the export and table column filters so each field can be turned on or off reliably.',
      },
      {
        ticket: 'BCX-323',
        title: 'Full Screen Mode For Live Avatar Agents',
        type: 'feature',
        description:
          'Added a dedicated full-screen embed for LiveAvatar agents with product images at `/embed/liveavatar-products-fullscreen/:agentId`. The layout uses the full viewport with no centered max-width box or empty margins, split avatar video and product panel edge-to-edge, and preserves the same LiveKit session flow, product matching, and dynamic variable query support as the standard products embed.',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-03-24',
    title: 'Conversation Flow Stability, LiveAvatar Enhancements & Analytics',
    items: [
      {
        ticket: 'BCX-326',
        title: 'Skip Response Node Fix',
        type: 'fix',
        description:
          'Resolved an issue in the Conversation Flow Builder where the Skip Response node was not executing correctly. Conversations that required skipping an agent response under certain conditions were instead stalling or producing duplicate replies, disrupting the intended flow path.',
      },
      {
        ticket: 'BCX-327',
        title: 'Logic Split Node Hallucinating',
        type: 'fix',
        description:
          'Fixed a critical issue where the Logic Split node was evaluating branching conditions incorrectly, causing conversations to follow the wrong path. The node was occasionally "hallucinating" condition matches, routing callers into unrelated branches and degrading the conversational experience.',
      },
      {
        ticket: 'BCX-322',
        title: 'Implement Change Logs',
        type: 'feature',
        description:
          'Introduced a dedicated Change Logs page within the platform, providing stakeholders with a centralized, organized view of all platform updates, improvements, and fixes. Each release is presented with high-level descriptions so teams can quickly understand what changed and why.',
      },
      {
        ticket: 'BCX-341',
        title: 'LiveAvatar Form Fields',
        type: 'enhancement',
        description:
          'Added form field support to the LiveAvatar experience, enabling avatar-driven conversations to collect structured user input through dynamic form elements. This allows LiveAvatar agents to gather information like names, emails, and preferences directly within the avatar interaction, without redirecting users elsewhere.',
      },
      {
        ticket: 'BCX-324',
        title: 'Call Average Response Time',
        type: 'feature',
        description:
          'Implemented average response time tracking and display for voice agent calls. Users now have visibility into how quickly their agents respond during live conversations, making it easier to monitor real-time performance, identify latency issues, and optimize agent configurations for faster interactions.',
      },
      {
        ticket: 'BCX-330',
        title: 'LiveAvatar Ecommerce Agent Cache Management Issue',
        type: 'fix',
        description:
          'Resolved a caching issue affecting LiveAvatar agents configured for ecommerce use cases. Stale product data and session context were being served to users, resulting in outdated pricing, incorrect product availability, and inconsistent conversation state across interactions.',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-03-16',
    title: 'Default Variables, LiveAvatar Cache Fix & Flow Builder Improvements',
    items: [
      {
        ticket: 'BCX-310',
        title: 'Default Variables Support',
        type: 'feature',
        description:
          'Added support for default variable values across agent configurations, ensuring that agents operate predictably even when dynamic variables are not provided at runtime. This reduces errors during testing and provides a safer fallback mechanism in production.',
      },
      {
        ticket: 'BCX-312',
        title: 'LiveAvatar Cache Fix',
        type: 'fix',
        description:
          'Addressed a caching issue in the LiveAvatar module where avatar sessions were occasionally loading stale configuration data, causing visual and behavioral inconsistencies during customer interactions.',
      },
      {
        ticket: 'BCX-315',
        title: 'Flow Builder Save Functionality',
        type: 'enhancement',
        description:
          'Improved the save mechanism in the Conversation Flow Builder to handle complex flow graphs more reliably. Previously, large flows with many nodes could fail to persist correctly, leading to data loss on reload.',
      },
      {
        ticket: 'BCX-318',
        title: 'Custom Function Fixes',
        type: 'fix',
        description:
          'Resolved multiple issues with Custom Function nodes in the Conversation Flow Builder, including incorrect parameter passing and timeout handling that caused flows to break mid-execution.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-03-11',
    title: 'LiveAvatar Video Agent & Product Module',
    items: [
      {
        ticket: 'BCX-300',
        title: 'LiveAvatar Video Agent Implementation',
        type: 'feature',
        description:
          'Launched the LiveAvatar Video Agent, enabling organizations to deploy AI-powered video avatars that handle customer conversations with visual presence. The video agent supports real-time lip-sync, gesture animation, and seamless integration with existing voice agent configurations.',
      },
      {
        ticket: 'BCX-305',
        title: 'Product Module',
        type: 'feature',
        description:
          'Introduced the Product Module for ecommerce-focused agents, allowing organizations to upload and manage product catalogs that agents can reference during conversations. Agents can now provide accurate product information, pricing, and availability in real time.',
      },
    ],
  },
];

const TypeBadge = ({ type }) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.feature;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color} ${config.border} border`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const WorkItem = ({ item }) => (
  <div className="group relative pl-5 py-4 border-b border-border/50 last:border-b-0 hover:bg-muted/20 transition-colors">
    <div className="absolute left-0 top-4 bottom-4 w-px bg-border group-hover:bg-primary/40 transition-colors" />
    <div className="absolute left-[-3px] top-[22px] w-[7px] h-[7px] rounded-full bg-border group-hover:bg-primary/60 transition-colors" />
    <div className="flex items-start gap-3 flex-wrap">
      <TypeBadge type={item.type} />
      <span className="text-xs font-mono text-muted-foreground bg-muted/40 px-2 py-1 rounded">
        {item.ticket}
      </span>
    </div>
    <h4 className="text-sm font-semibold text-foreground mt-2">{item.title}</h4>
    <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">{item.description}</p>
  </div>
);

const ReleaseCard = ({ release, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const fixCount = release.items.filter(i => i.type === 'fix').length;
  const featureCount = release.items.filter(i => i.type === 'feature').length;
  const enhancementCount = release.items.filter(i => i.type === 'enhancement' || i.type === 'performance' || i.type === 'security').length;

  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary flex-shrink-0">
          <Tag className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-foreground">v{release.version}</span>
            <span className="text-xs text-muted-foreground">{release.date}</span>
          </div>
          <p className="text-sm text-foreground/80 mt-0.5 truncate">{release.title}</p>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            {featureCount > 0 && (
              <span className="text-xs text-emerald-400">{featureCount} feature{featureCount > 1 ? 's' : ''}</span>
            )}
            {fixCount > 0 && (
              <span className="text-xs text-red-400">{fixCount} fix{fixCount > 1 ? 'es' : ''}</span>
            )}
            {enhancementCount > 0 && (
              <span className="text-xs text-blue-400">{enhancementCount} enhancement{enhancementCount > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <>
          <div className="h-px bg-border" />
          <div className="px-5 py-2">
            {release.items.map((item, idx) => (
              <WorkItem key={idx} item={item} />
            ))}
          </div>
          <div className="h-1 bg-gradient-to-r from-secondary-cyan via-primary to-secondary-cyan opacity-50" />
        </>
      )}
    </div>
  );
};

const ChangeLogsPage = () => {
  const [page, setPage] = useState(1);
  const listRef = useRef(null);
  const totalPages = Math.ceil(RELEASES.length / ITEMS_PER_PAGE);
  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const paginatedReleases = RELEASES.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  useEffect(() => {
    listRef.current?.scrollTo(0, 0);
  }, [page]);

  const latestRelease = RELEASES[0];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-10 flex-shrink-0 border-b border-border bg-card/95 backdrop-blur-sm px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Release Notes</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <User className="w-3 h-3" />
                {AUTHOR}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex min-h-0 p-6 pt-28">
        <div className="max-w-[1600px] mx-auto w-full flex gap-6 flex-col lg:flex-row">
          {/* Left: Latest release summary */}
          <aside className="flex-shrink-0 lg:w-[340px] lg:fixed lg:top-24 lg:left-[max(1.5rem,calc((100vw-1600px)/2+1.5rem))] lg:z-[5] lg:self-start">
            <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden h-fit">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border-b border-border">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Latest Release</span>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-bold text-primary">v{latestRelease.version}</span>
                  <span className="text-xs text-muted-foreground">{latestRelease.date}</span>
                </div>
                <p className="text-sm font-medium text-foreground/95 mb-3">{latestRelease.title}</p>
                <div className="space-y-2">
                  {latestRelease.items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <TypeBadge type={item.type} />
                      <span className="text-xs text-muted-foreground leading-relaxed pt-0.5">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-secondary-cyan via-primary to-secondary-cyan opacity-50" />
            </div>
          </aside>

          {/* Right: Release list */}
          <section className="flex-1 min-w-0 flex flex-col gap-4 lg:ml-[364px]" ref={listRef}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                All Releases ({RELEASES.length})
              </span>
            </div>

            {paginatedReleases.map((release, idx) => (
              <ReleaseCard
                key={release.version}
                release={release}
                defaultExpanded={idx === 0 && page === 1}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-card mt-2">
                <p className="text-xs text-muted-foreground">
                  Showing {startIdx + 1}-{Math.min(startIdx + ITEMS_PER_PAGE, RELEASES.length)} of {RELEASES.length} releases
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-border bg-card hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-muted-foreground px-2">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-border bg-card hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default ChangeLogsPage;
